import { DossierDetailsContent, DossierDetailsHeader, DossierDetailsPanel } from "@/app/pages/dossier/dossier-details-panel";
import { useGetMedewerkersOpStationAanvraag } from "@/entities/medewerkers-op-station";
import { RoleGate, UserRole } from "@/features/auth";
import { Card, ErrorDisplay, Separated, Skeleton } from "@/shared/components";
import { useArchiveVisibility } from "@/shared/hooks";
import { useParams } from "react-router";
import { MedewerkersOpStationDetailsLezen } from "../lezen";
import { MedewerkersOpStationDetailsRegievoerder } from "../regievoerder";

export function MedewerkersOpStationDetails() {
    const { id: dossierNummer } = useParams() as { id: string };
    const isArchived = useArchiveVisibility();

    const { data: aanvraag, isPending, error } = useGetMedewerkersOpStationAanvraag(dossierNummer);

    if (error)
        return (
            <DossierDetailsPanel>
                <DossierDetailsHeader section="MedewerkersOpStation" showBackButton />
                <DossierDetailsContent>
                    <ErrorDisplay error={error} title="Fout bij het laden van inzet medewerkers op station" />
                </DossierDetailsContent>
            </DossierDetailsPanel>
        );

    if (isPending || !aanvraag) {
        return <MedewerkersOpStationDetailsSkeleton />;
    }

    return (
        <DossierDetailsPanel>
            <DossierDetailsHeader section="MedewerkersOpStation" showBackButton />
            <DossierDetailsContent>
                {!isArchived && (
                    <>
                        <RoleGate roles={[UserRole.REGIEVOERDER]}>
                            <MedewerkersOpStationDetailsRegievoerder dossierNummer={dossierNummer} aanvraag={aanvraag} />
                        </RoleGate>
                        <RoleGate roles={[UserRole.SUPPORTCONSULTANT]}>
                            <MedewerkersOpStationDetailsLezen aanvraag={aanvraag} />
                        </RoleGate>
                    </>
                )}
                {isArchived && <MedewerkersOpStationDetailsLezen aanvraag={aanvraag} />}
            </DossierDetailsContent>
        </DossierDetailsPanel>
    );
}

function MedewerkersOpStationDetailsSkeleton() {
    return (
        <DossierDetailsPanel testId="vos-details-skeleton">
            <DossierDetailsHeader section="MedewerkersOpStation" showBackButton />
            <DossierDetailsContent>
                <div className="mb-8 flex items-center gap-1.5">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-40" />
                </div>

                <div className="mb-4 flex items-center justify-between font-medium">
                    <Skeleton className="h-6 w-40" />
                    <div className="flex gap-4">
                        <Skeleton className="h-8 w-45" />
                        <Skeleton className="h-8 w-40" />
                        <Skeleton className="h-8 w-37" />
                    </div>
                </div>

                <Card className="mb-8 rounded-xl py-0">
                    <ul>
                        <Separated>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <li key={index} className="flex items-center justify-between gap-4 px-4 py-3">
                                    <div className="flex min-w-0 flex-1 items-center gap-3">
                                        <Skeleton className="h-4 w-4" />
                                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                                            <Skeleton className="h-4 w-56" />
                                            <Skeleton className="h-3 w-36" />
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        <Skeleton className="h-7 w-24" />
                                        <Skeleton className="h-7 w-7 rounded-full" />
                                    </div>
                                </li>
                            ))}
                        </Separated>
                    </ul>
                </Card>

                <Skeleton className="mb-2 h-4 w-44" />
                <Skeleton className="h-10 w-44" />
            </DossierDetailsContent>
        </DossierDetailsPanel>
    );
}
